/**
 * useBookingState Hook
 * Centralized booking data state management
 */

import { useState, useCallback } from 'react';
import { BookingData, DEFAULT_MEETING_DURATION, DEFAULT_TIMEZONE } from '../utils/bookingConstants.js';

/**
 * `timeZone` intentionally starts at {@link DEFAULT_TIMEZONE} rather than the
 * browser's zone: this hook runs during SSR too, and seeding from
 * `Intl.DateTimeFormat()` there would render a different zone on the server
 * than on the client (hydration mismatch). `PublicBookingStepper` resolves the
 * real zone in a mount effect.
 */
function createInitialBookingData(): BookingData {
  return {
    email: '',
    name: '',
    description: '',
    location: '',
    isOnline: true,
    selectedSlot: null,
    bookeeEmail: '',
    meetingDuration: DEFAULT_MEETING_DURATION,
    timeZone: DEFAULT_TIMEZONE,
  };
}

export function useBookingState() {
  const [bookingData, setBookingData] = useState<BookingData>(createInitialBookingData);

  const updateBookingData = useCallback((updates: Partial<BookingData>) => {
    setBookingData((prev) => ({ ...prev, ...updates }));
  }, []);

  /**
   * Reset for "book another meeting". The resolved time zone is deliberately
   * PRESERVED — the booker has not moved, and re-detecting would discard an
   * explicit picker choice.
   */
  const resetBookingData = useCallback(() => {
    setBookingData((prev) => ({ ...createInitialBookingData(), timeZone: prev.timeZone }));
  }, []);

  return {
    bookingData,
    setBookingData,
    updateBookingData,
    resetBookingData,
  };
}
